/**
 * Small shared pieces of the field screens — the position cell, the filter
 * chip, and the engineer/date filter rows both registers share.
 *
 * ===========================================================================
 * THE POSITION CELL RENDERS THREE FACTS THREE WAYS (Rule 14)
 * ===========================================================================
 * `raw`        → the coordinates, 4 dp, with accuracy and the device-clock
 *                claim in the title. Worded from the device's mouth where it
 *                matters: the stamp is what the DEVICE REPORTED, not a court
 *                record of where a person stood.
 * `restricted` → the word "Restricted", in the house neutral chip — the same
 *                honesty as money's '•••' but readable. The marker itself is
 *                kept in the cell's title so what arrived is auditable.
 * `absent`     → "Not captured", muted, no chip: absence is not a secret, it
 *                is a nothing. GPS off and purged-under-retention both land
 *                here because the server sends both as `null` — the register
 *                cannot tell them apart and does not pretend to.
 *
 * ===========================================================================
 * THE ENGINEER FILTER IS DATA-DERIVED, AND WHY (recorded decision)
 * ===========================================================================
 * The colleague directory (`GET /users`) is behind `users.manage`, which the
 * seats that read these registers (Coordinator, Dev Head) do not hold —
 * firing it would be a guaranteed 403 (`use-directory.ts`'s C5 rule). The
 * engineers a register can OFFER are therefore the ones its rows carry:
 * distinct `user_id`/`user_name` pairs from the current result window, plus
 * whatever `?user_id=` the URL already holds (kept offered even when its rows
 * are not in view, so an applied filter is always clearable and never
 * unnameable). An engineer with no rows under the current date filter is not
 * offered — filtering to them would only produce the empty register the chips
 * already imply. Flagged in the handoff rather than solved with a second
 * directory mechanism tonight.
 */

import { useId } from 'react';
import { cn } from '../../lib/cn';
import { formatTime } from '../inquiries/format';
import {
  ARRIVAL_BAND_LABELS,
  classifyDistance,
  classifyPosition,
  formatAccuracy,
  formatCoordinates,
  formatDistanceM,
} from './position';
import type { ArrivalDistanceBand, MaskedDistance, MaskedPosition } from './api';

/* ──────────────────────────────────────────────────────────── position cell ── */

export function PositionCell({ value }: { value: MaskedPosition }): React.JSX.Element {
  const state = classifyPosition(value);
  if (state.kind === 'absent') {
    // `text-fg-secondary`, not muted: muted measures 2.63:1 on the light
    // surface (field.contrast.test.ts pins the refusal) and "never captured"
    // is a fact a reader must be able to read.
    return <span className="text-body13 text-fg-secondary">Not captured</span>;
  }
  if (state.kind === 'restricted') {
    return (
      <span
        title="This position is restricted for your account (•••)"
        className="inline-flex items-center whitespace-nowrap rounded-pill bg-subtle px-2.5 py-1 text-label font-medium text-fg ring-1 ring-line"
      >
        Restricted
      </span>
    );
  }
  const accuracy = formatAccuracy(state.view);
  return (
    <span
      className="font-mono text-mono text-fg-secondary"
      title={`Device reported ${state.view.lat}, ${state.view.lng} at ${state.view.captured_at} (server receipt ${state.view.recorded_at})`}
    >
      {formatCoordinates(state.view)}
      {accuracy === '' ? null : <span className="ml-1 text-fg-muted">{accuracy}</span>}
    </span>
  );
}

/* ──────────────────────────────────────────────── arrival distance (F-1) ── */

/**
 * THE ARRIVAL DISTANCE CELL — three states, like the position cell, for the
 * same Rule-14 reason (v17 F-1).
 *
 * `raw`    → the metres, for the engineer themself and for a `field.location.view`
 *            holder. Rendered `tnum` so a column of them lines up.
 * `banded` → the coarse band, for every other register seat. It reads as a
 *            phrase ("over 5 km"), NOT as a number, so nobody mistakes it for a
 *            measurement they can arithmetic on — three numbers is what
 *            trilaterated the engineer's home in the first place.
 * `absent` → nothing at all. No premises on file, or no stamp at arrive: there
 *            is no fact here and an empty cell is the honest render.
 *
 * ⚠ THE TITLE CARRIES NO NUMBER ON THE BANDED ARM. A tooltip is wire bytes like
 * any other; a `title` holding the raw metres would hand back exactly what the
 * cell was masked to withhold. The F-1 unit report names this as one of the
 * narrowing vectors that had to stay closed (sort key, ETag, tooltip).
 */
export function ArrivalDistance({
  value,
  band,
}: {
  value: MaskedDistance;
  band: ArrivalDistanceBand | null;
}): React.JSX.Element | null {
  const state = classifyDistance(value, band);
  if (state.kind === 'absent') return null;
  if (state.kind === 'banded') {
    return (
      <span
        data-distance-kind="banded"
        data-distance-band={state.band}
        title="Exact distance is restricted for your account (•••) — this is the recorded band"
        className="whitespace-nowrap text-body13 text-fg-secondary"
      >
        {ARRIVAL_BAND_LABELS[state.band]}
      </span>
    );
  }
  return (
    <span
      data-distance-kind="raw"
      data-distance-band={state.band ?? undefined}
      title="Distance from the customer's saved location at arrival"
      className="tnum whitespace-nowrap text-body13 text-fg-secondary"
    >
      {formatDistanceM(state.metres)}
    </span>
  );
}

/* ────────────────────────────────────────────────────────────── filter chip ── */

/** The Toolbar filter-chip geometry the registers share (6:467). */
export function FilterChip({
  label,
  active,
  onSelect,
}: {
  label: string;
  active: boolean;
  onSelect: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onSelect}
      className={cn(
        'flex h-8 items-center gap-1.5 rounded-sm border border-line bg-surface px-2.5 text-body13',
        active ? 'border-line-strong font-medium text-fg' : 'text-fg-secondary hover:text-fg',
      )}
    >
      {label}
    </button>
  );
}

/* ─────────────────────────────────────────────────────────── engineer chips ── */

export interface EngineerOption {
  id: string;
  name: string;
}

/** Distinct engineers present in the loaded rows, first-appearance order. */
export function engineerOptionsOf(
  rows: readonly { user_id: string; user_name: string | null }[],
): EngineerOption[] {
  const seen = new Map<string, EngineerOption>();
  for (const row of rows) {
    if (!seen.has(row.user_id)) {
      seen.set(row.user_id, { id: row.user_id, name: row.user_name ?? `Engineer #${row.user_id}` });
    }
  }
  return [...seen.values()];
}

export function EngineerFilterRow({
  options,
  active,
  onSelect,
}: {
  options: readonly EngineerOption[];
  /** The `?user_id=` in force, or null. */
  active: string | null;
  onSelect: (userId: string | null) => void;
}): React.JSX.Element | null {
  // The active id is offered even when its rows are not in the current window,
  // so an applied filter is always visible and clearable.
  const offered =
    active !== null && !options.some((option) => option.id === active)
      ? [...options, { id: active, name: `Engineer #${active}` }]
      : options;
  if (offered.length === 0 && active === null) return null;
  return (
    <div role="group" aria-label="Filter by engineer" className="flex flex-wrap items-center gap-2">
      <FilterChip label="All engineers" active={active === null} onSelect={() => onSelect(null)} />
      {offered.map((option) => (
        <FilterChip
          key={option.id}
          label={option.name}
          active={active === option.id}
          onSelect={() => onSelect(option.id)}
        />
      ))}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────── date pair ── */

export function DateRangeFilter({
  from,
  to,
  onChange,
}: {
  from: string;
  to: string;
  onChange: (next: { from: string; to: string }) => void;
}): React.JSX.Element {
  const fromId = useId();
  const toId = useId();
  const box =
    'h-8 rounded-sm border border-line bg-surface px-2 text-body13 text-fg [color-scheme:light] dark:[color-scheme:dark]';
  return (
    <div className="flex flex-wrap items-center gap-2">
      <label htmlFor={fromId} className="text-label font-medium text-fg-secondary">
        From
      </label>
      <input
        id={fromId}
        type="date"
        value={from}
        onChange={(event) => onChange({ from: event.target.value, to })}
        className={box}
      />
      <label htmlFor={toId} className="text-label font-medium text-fg-secondary">
        To
      </label>
      <input
        id={toId}
        type="date"
        value={to}
        onChange={(event) => onChange({ from, to: event.target.value })}
        className={box}
      />
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────── time cell ── */

/** "4:20 PM" with the exact instant in the title — viewer-local, stated as such. */
export function TimeCell({ iso }: { iso: string | null }): React.JSX.Element {
  if (iso === null) return <span className="text-fg-secondary">—</span>;
  return (
    <span className="tnum text-body13 text-fg" title={iso}>
      {formatTime(iso)}
    </span>
  );
}
